/**
 * Word Unscrambler — pure solving logic.
 *
 * The dictionary is INJECTED (never imported here) so this module is
 * unit-testable and safe to import anywhere — the large word list stays in the
 * server-side oRPC handler that passes it in.
 */

/** Keep only a–z, lowercased. */
export function normalizeLetters(input: string): string {
  return input.toLowerCase().replace(/[^a-z]/g, "");
}

function letterCounts(s: string): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const ch of s) counts[ch] = (counts[ch] ?? 0) + 1;
  return counts;
}

/** Can `word` be spelled using a subset of the `available` letter counts? */
function canForm(word: string, available: Record<string, number>): boolean {
  const need: Record<string, number> = {};
  for (const ch of word) {
    need[ch] = (need[ch] ?? 0) + 1;
    if (need[ch] > (available[ch] ?? 0)) return false;
  }
  return true;
}

/**
 * All dictionary words (length ≥ minLen) that can be spelled from a SUBSET of
 * the given letters — the classic "unscramble / words from letters". Sorted
 * longest-first, then alphabetically.
 */
export function findWords(
  letters: string,
  words: readonly string[],
  minLen = 2,
): string[] {
  const norm = normalizeLetters(letters);
  if (norm.length < minLen) return [];
  const available = letterCounts(norm);
  return words
    .filter(
      (w) =>
        w.length >= minLen && w.length <= norm.length && canForm(w, available),
    )
    .sort((a, b) => b.length - a.length || a.localeCompare(b));
}

/**
 * Exact anagrams: dictionary words that use ALL the letters exactly once each.
 * Sorted alphabetically.
 */
export function findAnagrams(
  letters: string,
  words: readonly string[],
): string[] {
  const norm = normalizeLetters(letters);
  if (!norm) return [];
  const key = norm.split("").sort().join("");
  return words
    .filter(
      (w) => w.length === norm.length && w.split("").sort().join("") === key,
    )
    .sort();
}

/** Group a sorted word list by length (descending) for display. */
export function groupByLength(
  words: string[],
): Array<{ length: number; words: string[] }> {
  const map = new Map<number, string[]>();
  for (const w of words) {
    const arr = map.get(w.length);
    if (arr) arr.push(w);
    else map.set(w.length, [w]);
  }
  return [...map.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([length, ws]) => ({ length, words: ws }));
}
